// Ассеты конвейера по манифесту (art/pipeline.md: «ID → файл ×1/×2, хеш, размер, пакет загрузки, рамка, привязка»).
// Манифест грузится лениво, после первого экрана, и в стартовый бандл не входит (tech/13 §2, критерий П п. 5).
// Нет манифеста, нет ID или файл не загрузился — компонент рисует запасной вариант: эмодзи, CSS или каркас
// (pipeline.md «Запасной рисунок»: пустого места и «битой картинки» игрок не увидит).
import { signal } from '@preact/signals';

/** Файл ассета: путь от public/ (как в манифесте арта) и плотность. */
export interface AssetFile { src: string; density: number; w?: number; h?: number; bytes?: number; sha?: string }
export interface AssetEntry {
  files: AssetFile[];
  /** Ступень загрузки ux/13 §8.2. */
  pack?: 'first' | 'stage2' | 'fight' | 'lazy';
  /** 9-slice: отступы рамки [верх, право, низ, лево] в px @1x (в манифесте арта — l/t/r/b, приводим). */
  slice?: [number, number, number, number];
  /** Фигуры — точка привязки [x, y]; фоны — сторона прижатия ('bottom', 'center'). */
  anchor?: [number, number] | string;
  fit?: string;
  use?: string;
  /** Слой куклы DOLL-*: левый верхний угол в px @1x внутри кадра FIG-hero-BODY (389 × 389), порядок z, слот. */
  pos?: [number, number];
  z?: number;
  slot?: string;
  /** Схема рига RIG-<id> для живой сцены боя: кадр (как у FIG) и части по возрастанию z; pos и pivot — px @1x кадра. */
  frame?: [number, number];
  parts?: RigPartDesc[];
  /** Спрайт-лист эффекта FX-*: кадры ленты в px @1x, частота, цикл. */
  frames?: { x: number; y: number; w: number; h: number }[];
  fps?: number;
  loop?: boolean;
  /** Стадия, заменяющая нарисованное на фоне (ворота): прямоугольник [x0, y0, x1, y1] в долях картинки фона, по ID фона. */
  bgRect?: Record<string, [number, number, number, number]>;
  /** Куда смотрит фигура на рисунке (FIG-*, RIG-*): герой — вправо, боссы — влево, к герою. */
  facing?: 'left' | 'right';
  /** Масштаб кадра фигуры относительно FIG-hero (кран больше крысы): боссу k = k героя × scale. */
  scale?: number;
  /** Точка зоны на XL-фоне: [x, y] в долях картинки BG-GARAGE-XL (стадии ZONE-*); без поля — точка из content/garage. */
  posXl?: [number, number];
}
export interface RigPartDesc { part: string; id: string; z: number; parent: string | null; pos: [number, number]; pivot: [number, number] }
export type Manifest = Record<string, AssetEntry>;

export const ART_BASE = 'assets/art/';
/** Пустой, пока не загрузился; версия — для перерисовки компонентов, которые ждут ассет. */
export const manifest = signal<Manifest>({});
export const manifestLoaded = signal(false);
const broken = new Set<string>();

const safe = (p: string) => !p.includes('..') && !/^[a-z]+:/i.test(p) && !p.startsWith('/');
const densityOf = (key: string | undefined, path: string) => {
  const k = key?.match(/^(\d)x$/)?.[1];
  if (k) return Number(k);
  return /@3x\./.test(path) ? 3 : /@2x\./.test(path) ? 2 : 1;
};

function normFiles(raw: unknown): AssetFile[] {
  const out: AssetFile[] = [];
  const add = (key: string | undefined, f: Record<string, unknown>) => {
    // формат арта: {path: 'assets/art/bg/X@1x.webp'} от public/; формат пилота: {file: 'X@1x.webp'} от assets/art/
    const src = typeof f['path'] === 'string' ? f['path'] : typeof f['file'] === 'string' ? ART_BASE + f['file'] : null;
    if (!src || !safe(src)) return;
    out.push({ src, density: densityOf(key, src), ...(typeof f['w'] === 'number' ? { w: f['w'] } : {}), ...(typeof f['h'] === 'number' ? { h: f['h'] } : {}) });
  };
  if (Array.isArray(raw)) for (const f of raw) { if (f && typeof f === 'object') add(undefined, f as Record<string, unknown>); }
  else if (raw && typeof raw === 'object') for (const [k, f] of Object.entries(raw)) { if (f && typeof f === 'object') add(k, f as Record<string, unknown>); }
  return out;
}

function normSlice(raw: unknown): AssetEntry['slice'] | undefined {
  if (Array.isArray(raw) && raw.length === 4 && raw.every((n) => typeof n === 'number')) {
    const [l, t, r, b] = raw as number[]; // порядок манифеста арта: l/t/r/b
    return [t!, r!, b!, l!];
  }
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, number>;
    if (['l', 't', 'r', 'b'].every((k) => typeof o[k] === 'number')) return [o['t']!, o['r']!, o['b']!, o['l']!];
  }
  return undefined;
}

/**
 * Соответствие ID клиента → ID в манифесте арта (docs/art-prod). Файлы не переименовываются: если нашего ID
 * в манифесте нет, берётся ассет арта под его именем. Одно место для всех расхождений имён.
 */
export const ART_ALIASES: Readonly<Record<string, string>> = {
  'ICO-NUTS': 'CUR-nuts', 'ICO-DIESEL': 'CUR-diesel', 'ICO-ENERGY': 'CUR-energy', 'ICO-KEYS': 'CUR-keys',
  'ICO-TALONS': 'CUR-talons', 'ICO-TICKETS': 'CUR-tickets', 'ICO-SCRAP': 'CUR-scrap', 'ICO-XP': 'CUR-xp',
  'ICO-NAV-HOME': 'NAV-HOME', 'ICO-NAV-MAP': 'NAV-MAP', 'ICO-NAV-SHOP': 'NAV-SHOP', 'ICO-NAV-CHAR': 'NAV-CHAR',
  'BTN-ACCENT': 'UI-BTN-PRIMARY', 'BTN-OUTLINE': 'UI-BTN-SECONDARY',
  'PANEL-CARD': 'UI-CARD', 'PANEL-SHEET': 'UI-MODAL', 'PANEL-HEADER': 'UI-HEADER', 'PANEL-CHIP': 'UI-CHIP',
  'PANEL-WOOD': 'UI-PANEL-WOOD', 'PANEL-TIN': 'UI-PANEL-TIN', 'PANEL-PAPER': 'UI-PANEL-PAPER',
};

const pair = (v: unknown): [number, number] | null => (Array.isArray(v) && v.length === 2 && v.every((n) => typeof n === 'number') ? [v[0] as number, v[1] as number] : null);
function normBgRect(raw: Record<string, unknown>): Record<string, [number, number, number, number]> {
  const out: Record<string, [number, number, number, number]> = {};
  for (const [k, v] of Object.entries(raw)) if (Array.isArray(v) && v.length === 4 && v.every((n) => typeof n === 'number')) out[k] = v as [number, number, number, number];
  return out;
}

/** Части рига: без id, шарнира или места — часть отбрасывается (сцена тогда возьмёт цельную фигуру FIG). */
function normParts(raw: unknown[]): RigPartDesc[] {
  const out: RigPartDesc[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    const pos = pair(o['pos']);
    const pivot = pair(o['pivot']);
    if (typeof o['part'] !== 'string' || typeof o['id'] !== 'string' || !pos || !pivot) continue;
    out.push({ part: o['part'], id: o['id'], z: typeof o['z'] === 'number' ? o['z'] : 0, parent: typeof o['parent'] === 'string' ? o['parent'] : null, pos, pivot });
  }
  return out;
}

/** Разбор манифеста: `{version, assets}` (формат арта или наш) или формат пилота `{ID: {files}}`. Мусор отбрасывается. */
export function parseManifest(raw: unknown): Manifest {
  if (!raw || typeof raw !== 'object') return {};
  const obj = raw as Record<string, unknown>;
  const src = obj['assets'] && typeof obj['assets'] === 'object' ? (obj['assets'] as Record<string, unknown>) : obj;
  const out: Manifest = {};
  for (const [id, v] of Object.entries(src)) {
    if (!v || typeof v !== 'object') continue;
    const e = v as Record<string, unknown>;
    const files = normFiles(e['files']);
    if (!files.length) continue;
    const slice = normSlice(e['slice']);
    const anchor = e['anchor'];
    out[id] = {
      files,
      ...(typeof e['pack'] === 'string' ? { pack: e['pack'] as AssetEntry['pack'] } : {}),
      ...(slice ? { slice } : {}),
      ...(typeof anchor === 'string' || Array.isArray(anchor) ? { anchor: anchor as AssetEntry['anchor'] } : {}),
      ...(typeof e['fit'] === 'string' ? { fit: e['fit'] } : {}),
      ...(typeof e['use'] === 'string' ? { use: e['use'] } : {}),
      ...(Array.isArray(e['pos']) && e['pos'].length === 2 ? { pos: [Number(e['pos'][0]), Number(e['pos'][1])] as [number, number] } : {}),
      ...(typeof e['z'] === 'number' ? { z: e['z'] } : {}),
      ...(typeof e['slot'] === 'string' ? { slot: e['slot'] } : {}),
      ...(Array.isArray(e['frame']) && e['frame'].length === 2 ? { frame: [Number(e['frame'][0]), Number(e['frame'][1])] as [number, number] } : {}),
      ...(Array.isArray(e['parts']) ? { parts: normParts(e['parts']) } : {}),
      ...(Array.isArray(e['frames']) ? { frames: (e['frames'] as Record<string, unknown>[]).filter((f) => f && ['x', 'y', 'w', 'h'].every((k) => typeof f[k] === 'number')).map((f) => ({ x: f['x'] as number, y: f['y'] as number, w: f['w'] as number, h: f['h'] as number })) } : {}),
      ...(typeof e['fps'] === 'number' ? { fps: e['fps'] } : {}),
      ...(typeof e['loop'] === 'boolean' ? { loop: e['loop'] } : {}),
      ...(e['bg_rect'] && typeof e['bg_rect'] === 'object' ? { bgRect: normBgRect(e['bg_rect'] as Record<string, unknown>) } : {}),
      ...(e['facing'] === 'left' || e['facing'] === 'right' ? { facing: e['facing'] } : {}),
      ...(typeof e['scale'] === 'number' && e['scale'] > 0 ? { scale: e['scale'] } : {}),
      ...(pair(e['pos_xl']) ? { posXl: pair(e['pos_xl'])! } : {}),
    };
  }
  for (const [ours, theirs] of Object.entries(ART_ALIASES)) if (!out[ours] && out[theirs]) out[ours] = out[theirs];
  return out;
}

/** Файл нужной плотности: @2x на экранах с dpr ≥ 1.5, иначе @1x; чего нет — ближайший. */
export function pickFile(e: AssetEntry, dpr: number): AssetFile {
  const want = dpr > 1.5 ? 2 : 1;
  return [...e.files].sort((a, b) => Math.abs(a.density - want) - Math.abs(b.density - want) || b.density - a.density)[0]!;
}

export function assetUrl(id: string, m: Manifest = manifest.value, dpr = globalThis.devicePixelRatio ?? 1, base = import.meta.env?.BASE_URL ?? '/'): string | null {
  const e = m[id];
  if (!e || broken.has(id)) return null;
  return `${base}${pickFile(e, dpr).src}`;
}

/** Вариант `<ID>-XL` для ширины ≥ 1024 (сцена 16:10, ux/12 §1), если он есть; иначе обычный. */
export function bgId(id: string, width: number, m: Manifest = manifest.value): string {
  return width >= 1024 && m[`${id}-XL`] && !broken.has(`${id}-XL`) ? `${id}-XL` : id;
}

export function assetEntry(id: string): AssetEntry | null {
  return broken.has(id) ? null : manifest.value[id] ?? null;
}

/** Файл не загрузился — дальше показываем запасной вариант (и отчёт об ошибке ассета ловит reporter). */
export function markBroken(id: string): void {
  if (broken.has(id)) return;
  broken.add(id);
  manifest.value = { ...manifest.value };
}

/** Загрузка манифеста; 404 или сеть — пустой манифест, игра работает на запасных вариантах. */
export async function loadManifest(fetchImpl: typeof fetch = fetch): Promise<Manifest> {
  try {
    const res = await fetchImpl(`${import.meta.env.BASE_URL}${ART_BASE}manifest.json`, { cache: 'no-cache' });
    if (res.ok) manifest.value = parseManifest(await res.json());
  } catch { /* остаёмся на запасных */ }
  manifestLoaded.value = true;
  return manifest.value;
}

/** Предзагрузка пакета (ступени) фоном: браузер кладёт файлы в кэш, экран берёт их без задержки. */
export function preloadPack(pack: NonNullable<AssetEntry['pack']>, m: Manifest = manifest.value): string[] {
  const urls = Object.keys(m).filter((id) => m[id]!.pack === pack).map((id) => assetUrl(id, m)).filter((u): u is string => !!u);
  for (const u of urls) { const img = new Image(); img.decoding = 'async'; img.src = u; }
  return urls;
}
